import React, {useState, useRef, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {User, LogOut, ChevronDown} from 'lucide-react';
import Api from '../../Api';
import UserSettingsDialog from '../UserSettingsDialog/UserSettingsDialog';

interface UserData {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  address?: {
    streetAddress?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    mobilePhone?: string;
    businessPhone?: string;
  };
  workInformation?: {
    companyName?: string;
    department?: string;
    jobTitle?: string;
  };
}

interface UserDropdownProps {
  userEmail?: string;
  userName?: string;
  userData?: UserData;
}

export default function UserDropdown({userEmail = 'user@example.com', userName = 'User', userData}: UserDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    // Clear authentication tokens
    Api.logout();
    // Redirect to login page
    navigate('/admin/auth/login-page');
    setIsOpen(false);
  };

  const handleUserSettings = () => {
    setIsOpen(false);
    setSettingsOpen(true);
  };

  const initials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        aria-label="User menu"
      >
        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-sm font-semibold">
          {initials}
        </div>
        <div className="hidden md:block text-left">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{userName}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{userEmail}</p>
        </div>
        <ChevronDown size={16} className={`text-gray-500 dark:text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 rounded-lg shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
          {/* User Info Section */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-sm font-semibold">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{userName}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{userEmail}</p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={handleUserSettings}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <User size={16} className="mr-3" />
              <span>User Settings</span>
            </button>
          </div>

          {/* Logout Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 py-1">
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <LogOut size={16} className="mr-3" />
              <span>Log out</span>
            </button>
          </div>
        </div>
      )}

      {/* User Settings Dialog */}
      {userData && <UserSettingsDialog user={userData} isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}
